import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import SDRLayout from "../../components/sdr/SDRLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Loader2, CalendarDays, CheckCircle2 } from "lucide-react";

const SDRCalendar = () => {
  const { API, token, user } = useAuth();
  const [calLink, setCalLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [justBooked, setJustBooked] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/sdr/calendar-link`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const d = await r.json();
          let link = d.calendar_link || "";
          // Append SDR agent code as UTM so Calendly webhook can attribute the booking
          if (link && user?.agent_code) {
            const separator = link.includes("?") ? "&" : "?";
            link = `${link}${separator}utm_source=${encodeURIComponent(user.agent_code)}`;
          }
          setCalLink(link);
        }
      } catch (err) {
        console.error("Failed to load calendar link:", err);
    }
      setLoading(false);
    })();
  }, [API, token, user]);

  // Listen for Calendly booking confirmation via postMessage
  const handleCalendlyMessage = useCallback(async (e) => {
    if (e.origin !== "https://calendly.com" && e.origin !== "https://assets.calendly.com") return;
    if (!e.data?.event || e.data.event !== "calendly.event_scheduled") return;

    const payload = e.data.payload || {};
    const invitee = payload.invitee || {};
    const event = payload.event || {};

    // Extract date from the event URI or use today
    let bookingDate = new Date().toISOString().split("T")[0];
    if (event.start_time) {
      bookingDate = new Date(event.start_time).toISOString().split("T")[0];
    }

    const clientName = invitee.name || "Calendly Booking";
    const clientEmail = invitee.email || "";

    try {
      const r = await fetch(`${API}/sdr/appointments`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          client_name: clientName,
          client_email: clientEmail,
          date: bookingDate,
          notes: "Auto-logged from Calendly booking",
        }),
      });
      if (r.ok) {
        setJustBooked(true);
        toast.success("Appointment auto-logged from Calendly booking!");
        setTimeout(() => setJustBooked(false), 5000);
      }
    } catch (err) {
      console.error("Calendly auto-log failed:", err);
    }
  }, [API, token]);

  useEffect(() => {
    window.addEventListener("message", handleCalendlyMessage);
    return () => window.removeEventListener("message", handleCalendlyMessage);
  }, [handleCalendlyMessage]);

  if (loading) return <SDRLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div></SDRLayout>;

  return (
    <SDRLayout>
      <div className="space-y-5" data-testid="sdr-calendar">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-[#1A2636]">Book Appointment</h1>
          {justBooked && (
            <Badge className="bg-green-100 text-green-700 flex items-center gap-1.5 animate-in fade-in" data-testid="booking-confirmed-badge">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Appointment Logged
            </Badge>
          )}
        </div>

        {calLink ? (
          <Card className="border-0 shadow-sm overflow-hidden">
            <CardContent className="p-0">
              <iframe
                src={calLink}
                title="Booking Calendar"
                className="w-full border-0"
                style={{ minHeight: "680px" }}
                data-testid="calendar-iframe"
              />
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <CalendarDays className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <h2 className="font-semibold text-[#1A2636] mb-1">Calendar Not Configured</h2>
              <p className="text-sm text-gray-500">Your admin hasn't set up the booking calendar yet. Please reach out to your manager.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </SDRLayout>
  );
};

export default SDRCalendar;
